import {useEffect, useMemo, useState} from 'react';
import {
  ANNOUNCE_COLLECTION,
  DELETE_COLLECTION,
} from 'graphql/mutations/collections';
import {NotificationOutlined} from '@ant-design/icons';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import moment from 'moment';
import {Input, Pagination, Select, Spin, Table, Tooltip} from 'antd';
import {useMutation, useQuery} from '@apollo/client';
import {toast} from 'react-toastify';
import {useNavigate} from 'react-router-dom';
import {GET_ALL_COLLECTIONS} from 'graphql/queries/collections';
import {GET_ALL_STOCKS} from 'graphql/queries/stocks';
import {Button} from '@dabafinance/react-components';
import styles from './collectionsUI.module.scss';
import {NavTabsEnum} from 'utils/constants';
import {
  makeSelectSubTabOperations,
  makeSelectTabOperations,
} from 'redux/store/auth';
import {useSelector} from 'react-redux';

const SORTS = [
  {value: 'CREATED_AT', label: 'Newest first'},
  {value: 'NAME', label: 'Name'},
  {value: 'MOST_TRADED', label: 'Most traded'},
  {value: 'RETURN', label: 'Return'},
];

/**
 * Collections list.
 *
 * The table had no search or filter of any kind, so finding one collection
 * among many meant paging. `CollectionFilters` already supports search,
 * status, exchange and sort, so all four are served by the API rather than
 * filtered client-side over a single page.
 */
const CollectionOverview = () => {
  const [limit, setLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [collectionId, setCollectionId] = useState(null);
  const [collectionName, setCollectionName] = useState('');
  const [announcementTarget, setAnnouncementTarget] = useState(null);
  const [announceCollection, {loading: announcing}] =
    useMutation(ANNOUNCE_COLLECTION);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState();
  const [exchange, setExchange] = useState();
  const [sortBy, setSortBy] = useState('CREATED_AT');

  const [deleteCollection, {loading: loadingDelete}] =
    useMutation(DELETE_COLLECTION);
  const navigate = useNavigate();

  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.Collections),
  );
  const collectionOperations = useSelector(state =>
    makeSelectSubTabOperations(
      state,
      NavTabsEnum.Collections,
      'collections-list',
    ),
  );

  // Typing must not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setCurrentPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  const filters = useMemo(
    () => ({
      paginate: true,
      page: currentPage,
      limit,
      sortBy,
      sortDirection: sortBy === 'NAME' ? 'ASC' : 'DESC',
      ...(debouncedSearch && {search: debouncedSearch}),
      ...(status && {status}),
      ...(exchange && {exchanges: [exchange]}),
    }),
    [currentPage, limit, sortBy, debouncedSearch, status, exchange],
  );

  const {loading, refetch, data} = useQuery(GET_ALL_COLLECTIONS, {
    variables: {filters},
    errorPolicy: 'all',
    onError: error => toast.error(error?.message),
  });

  const {data: exchangeData} = useQuery(GET_ALL_STOCKS, {
    errorPolicy: 'all',
  });

  const handleDelete = () => {
    deleteCollection({variables: {collectionId}})
      .then(({data: result}) => {
        if (result?.deleteCollection?.__typename === 'Error')
          return toast.error(result?.deleteCollection?.message);
        toast.success(`${collectionName} deleted`);
        setOpenConfirmModal(false);
        refetch();
      })
      .catch(error => toast.error(error?.message));
  };

  const handleAnnounce = async () => {
    if (
      !collectionOperations?.update ||
      announcementTarget?.status !== 'Active' ||
      announcing
    )
      return;
    try {
      const {data: result} = await announceCollection({
        variables: {
          collectionId: announcementTarget.id,
          ...(announcementTarget.announcedAt && {again: true}),
        },
      });
      const response = result?.announceCollection;
      if (response?.__typename !== 'Response') {
        toast.error(response?.message || 'Could not confirm the announcement.');
        return;
      }
      toast.success(response.message);
      setAnnouncementTarget(null);
      await refetch();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // A successful delete is a soft delete. adminGetAllCollections does not
  // reliably exclude those rows (BACKEND_FINDINGS 2.11), so filter here too.
  const visibleCollections = (
    data?.adminGetAllCollections?.data?.collections ?? []
  ).filter(collection => !collection?.isDeleted);

  const total = data?.adminGetAllCollections?.data?.pagination?.total ?? 0;
  const filtered = Boolean(debouncedSearch || status || exchange);

  const resetFilters = () => {
    setSearch('');
    setStatus(undefined);
    setExchange(undefined);
    setSortBy('CREATED_AT');
    setCurrentPage(1);
  };

  const columns = [
    {
      title: 'Collection',
      dataIndex: 'name',
      key: 'name',
      width: 260,
      render: (text, record) => (
        <div className={styles.nameCell}>
          {record?.iconUrl ? (
            <img src={record.iconUrl} alt="" className={styles.nameIcon} />
          ) : (
            <div className={`${styles.nameIcon} ${styles.nameIconFallback}`}>
              {(record?.name ?? '?').slice(0, 2).toUpperCase()}
            </div>
          )}
          <div>
            <span className={styles.cellName}>{record?.name}</span>
            <span className={styles.cellSub}>
              {record?.ticker ?? '—'}
              {record?.industry?.name ? ` · ${record.industry.name}` : ''}
            </span>
          </div>
        </div>
      ),
    },
    {
      title: 'Exchange',
      dataIndex: 'exchange',
      key: 'exchange',
      width: 120,
      render: (text, record) =>
        record?.exchange?.code ? (
          <Tooltip title={record.exchange.stockExchangeName}>
            <span className={`${styles.chip} ${styles.chipBrand}`}>
              {record.exchange.code}
            </span>
          </Tooltip>
        ) : (
          <span className={styles.cellMuted}>—</span>
        ),
    },
    {
      title: 'Constituents',
      dataIndex: 'instrumentCount',
      key: 'instrumentCount',
      width: 130,
      align: 'right',
      render: (text, record) => (
        <span className={styles.cellNum}>{record?.instrumentCount ?? 0}</span>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (text, record) => (
        // Was `DD MMM, YYYY. H:mm A`, which printed a 24-hour hour beside an
        // AM/PM suffix — "18:06 PM".
        <Tooltip title={moment(record?.createdAt).format('D MMMM YYYY, HH:mm')}>
          <span className={styles.cellMuted}>
            {moment(record?.createdAt).format('D MMM YYYY')}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (text, record) => (
        <span
          className={`${styles.chip} ${styles.chipDot} ${
            record?.status === 'Active' ? styles.chipOk : styles.chipMute
          }`}>
          {record?.status === 'Active' ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      title: 'Announced',
      dataIndex: 'announcedAt',
      key: 'announcedAt',
      width: 155,
      render: value =>
        value ? (
          <Tooltip title={moment.utc(value).format('D MMMM YYYY, HH:mm [UTC]')}>
            <span className={styles.cellMuted}>
              {moment.utc(value).format('D MMM YYYY')}
            </span>
          </Tooltip>
        ) : (
          <Tooltip title="Announcement tracking began on 24 October 2026. Earlier announcements may not be recorded.">
            <span className={styles.cellMuted}>Not announced</span>
          </Tooltip>
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 225,
      render: (text, record) => (
        <div className={styles.rowActions}>
          <Tooltip
            title={
              !collectionOperations?.update
                ? 'No announcement access'
                : record.status !== 'Active'
                ? 'Switch the collection on first'
                : record.announcedAt
                ? 'Announce again'
                : 'Announce'
            }>
            <span>
              <button
                type="button"
                className={styles.announceButton}
                aria-label={`${
                  record.announcedAt ? 'Announce again' : 'Announce'
                } ${record.name}`}
                disabled={
                  !collectionOperations?.update ||
                  record.status !== 'Active' ||
                  announcing
                }
                onClick={() => setAnnouncementTarget(record)}>
                <NotificationOutlined aria-hidden="true" />
                {record.announcedAt ? 'Again' : 'Announce'}
              </button>
            </span>
          </Tooltip>
          <Tooltip title="View constituents">
            <button
              type="button"
              className={styles.iconButton}
              onClick={() =>
                navigate(`/dashboard/collections/${record?.id}/constituents`)
              }>
              <EyeIcon />
            </button>
          </Tooltip>
          <Tooltip title={tabOperations?.update ? 'Edit' : 'No edit access'}>
            <button
              type="button"
              className={styles.iconButton}
              disabled={!tabOperations?.update}
              onClick={() =>
                navigate(`/dashboard/collections/update/${record?.id}`)
              }>
              <PencilIcon />
            </button>
          </Tooltip>
          <Tooltip
            title={tabOperations?.delete ? 'Delete' : 'No delete access'}>
            <button
              type="button"
              className={`${styles.iconButton} ${styles.iconButtonDanger}`}
              disabled={!tabOperations?.delete}
              onClick={() => {
                setCollectionId(record?.id);
                setCollectionName(record?.name);
                setOpenConfirmModal(true);
              }}>
              <TrashIcon />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className={styles.panel}>
        <div className={styles.toolbar}>
          <div className={styles.toolbarSearch}>
            <Input
              allowClear
              placeholder="Search by name or ticker"
              value={search}
              onChange={event => setSearch(event.target.value)}
            />
          </div>
          <Select
            allowClear
            placeholder="Any exchange"
            style={{minWidth: 150}}
            value={exchange}
            onChange={value => {
              setExchange(value);
              setCurrentPage(1);
            }}>
            {exchangeData?.getAllStocksExchange?.map(item => (
              <Select.Option key={item?.id} value={item?.id}>
                {item?.code}
              </Select.Option>
            ))}
          </Select>
          <Select
            allowClear
            placeholder="Any status"
            style={{minWidth: 130}}
            value={status}
            onChange={value => {
              setStatus(value);
              setCurrentPage(1);
            }}>
            <Select.Option value="Active">Active</Select.Option>
            <Select.Option value="InActive">Inactive</Select.Option>
          </Select>
          <Select
            style={{minWidth: 150}}
            value={sortBy}
            onChange={value => {
              setSortBy(value);
              setCurrentPage(1);
            }}>
            {SORTS.map(option => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
          {filtered && (
            <button
              type="button"
              className={styles.toolbarReset}
              onClick={resetFilters}>
              Clear
            </button>
          )}
          <div className={styles.toolbarSpacer} />
          <span className={styles.toolbarCount}>
            <b>{total}</b> {total === 1 ? 'collection' : 'collections'}
          </span>
        </div>

        {loading ? (
          <div className={styles.loading}>
            <Spin size="large" />
            <h1>Loading collections…</h1>
          </div>
        ) : visibleCollections.length === 0 ? (
          <div className={styles.empty}>
            <h3>
              {filtered
                ? 'No collections match those filters'
                : 'No collections yet'}
            </h3>
            <p>
              {filtered
                ? 'Try a different search term, or clear the filters to see everything.'
                : 'A collection groups listed public equities from one exchange into a single basket an investor can buy in one order.'}
            </p>
            {filtered ? (
              <Button
                label="Clear filters"
                type="secondary"
                onClick={resetFilters}
              />
            ) : (
              <Button
                label="Create collection"
                disabled={!tabOperations?.create}
                onClick={() => navigate('/dashboard/collections/new')}
              />
            )}
          </div>
        ) : (
          <>
            <div className={styles.table}>
              <Table
                scroll={{x: 1150}}
                dataSource={visibleCollections}
                columns={columns}
                rowKey={record => record?.id}
                pagination={false}
              />
            </div>
            {total > limit && (
              <div className={styles.tableFoot}>
                <Pagination
                  current={currentPage}
                  onChange={page => setCurrentPage(page)}
                  total={total}
                  showSizeChanger
                  pageSize={limit}
                  onShowSizeChange={(_, size) => {
                    setLimit(size);
                    setCurrentPage(1);
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmModal
        openModal={Boolean(announcementTarget)}
        setOpenModal={open => {
          if (!open && !announcing) setAnnouncementTarget(null);
        }}
        title={
          announcementTarget?.announcedAt
            ? 'Announce again?'
            : 'Announce collection'
        }
        confirmText={`Announce ${
          announcementTarget?.name || 'this collection'
        }${
          announcementTarget?.announcedAt ? ' again' : ''
        } to all users? This sends a push and posts to social channels.`}
        loading={announcing}
        handleOperation={handleAnnounce}
      />

      <ConfirmModal
        openModal={openConfirmModal}
        setOpenModal={setOpenConfirmModal}
        title={'Delete collection'}
        confirmText={`Delete "${collectionName}"? Investors will no longer see it. This is a soft delete, so the record is retained.`}
        confirmation={true}
        loading={loadingDelete}
        handleOperation={handleDelete}
      />
    </>
  );
};

/* Inline so the row actions are legible at 30px without shipping icon files
   that only ever appear here. */
const EyeIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true">
    <path
      d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const PencilIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true">
    <path
      d="M4 20h4L19 9a2.1 2.1 0 0 0-3-3L5 17v3Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

const TrashIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true">
    <path
      d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13M10 11v6M14 11v6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default CollectionOverview;
